import React, { useMemo, useState } from "react";
import DcBarriersCriteriaCard from "./DcBarriersCriteriaCard";
import type {
  DcBarriersPanelProps,
  DcBarriersStatusFilter,
} from "./types";

const FILTER_OPTIONS: { value: DcBarriersStatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "met", label: "Met" },
  { value: "not_met", label: "Not Met" },
];

const DcBarriersPanel: React.FC<DcBarriersPanelProps> = ({
  data,
  finalizedDisposition,
  onEvidenceClick,
}) => {
  const [statusFilter, setStatusFilter] =
    useState<DcBarriersStatusFilter>("all");

  const entry = useMemo(() => {
    if (!data.length) return null;
    if (finalizedDisposition) {
      return (
        data.find((item) => item.Setting === finalizedDisposition) ?? data[0]
      );
    }
    return data[0];
  }, [data, finalizedDisposition]);

  const requirements = entry?.Requirements ?? [];
  const dispositionLabel = finalizedDisposition || entry?.Setting || null;

  const metCount = requirements.filter((r) => r.Status === "Met").length;
  const notMetCount = requirements.length - metCount;
  const dischargeReady = requirements.length > 0 && notMetCount === 0;

  const indexedRequirements = useMemo(
    () =>
      requirements.map((requirement, index) => ({
        requirement,
        serialNumber: index + 1,
      })),
    [requirements]
  );

  const visibleRequirements = useMemo(() => {
    if (statusFilter === "met") {
      return indexedRequirements.filter(
        ({ requirement }) => requirement.Status === "Met"
      );
    }
    if (statusFilter === "not_met") {
      return indexedRequirements.filter(
        ({ requirement }) => requirement.Status === "Not Met"
      );
    }
    return indexedRequirements;
  }, [indexedRequirements, statusFilter]);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-white">
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden px-4 pb-3 pt-1">
        <div className="flex shrink-0 items-stretch gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-3 rounded-lg border border-gray-300 bg-gray-100 p-2.5 shadow-sm">
            <div className="min-w-0">
              <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                Finalized Discharge Disposition
              </p>
              {dispositionLabel ? (
                <span className="mt-1 inline-flex rounded-md border border-emerald-800 bg-emerald-700 px-3 py-1 text-lg font-bold text-white shadow-sm">
                  {dispositionLabel}
                </span>
              ) : (
                <p className="mt-1 text-sm text-slate-500">
                  No finalized disposition selected.
                </p>
              )}
            </div>

            <div className="ml-auto flex shrink-0 items-end gap-2">
              <div className="rounded-md border border-green-200 bg-green-200 px-2.5 py-1 text-center">
                <p className="text-[9px] font-medium uppercase tracking-wide text-slate-500">
                  Requirements Met
                </p>
                <p className="text-xl font-black tabular-nums text-emerald-700">
                  {metCount}
                </p>
              </div>

              {notMetCount > 0 && (
                <div className="rounded-md border border-red-200 bg-red-50 px-2.5 py-1 text-center">
                  <p className="text-[9px] font-medium uppercase tracking-wide text-slate-500">
                    Requirements Not Met
                  </p>
                  <p className="text-xl font-black tabular-nums text-red-700">
                    {notMetCount}
                  </p>
                </div>
              )}

              {dischargeReady && (
                <div className="rounded-md border border-green-200 bg-green-200 px-2.5 py-1 text-center">
                  <p className="text-[9px] font-medium uppercase tracking-wide text-slate-500">
                    Status
                  </p>
                  <p className="text-xl font-black text-emerald-700">Ready</p>
                </div>
              )}
            </div>
          </div>

          <div className="shrink-0 rounded-lg border border-gray-200 bg-white p-2.5 shadow-sm">
            <p className="mb-1.5 text-center text-[10px] font-medium uppercase tracking-wide text-slate-500">
              Filter
            </p>
            <div className="grid grid-cols-3 rounded-md border border-slate-200 bg-slate-100 p-0.5">
              {FILTER_OPTIONS.map((option) => {
                const isActive = statusFilter === option.value;
                let activeClass = "bg-white text-gray-900 shadow-sm";
                if (isActive && option.value === "met") {
                  activeClass = "bg-emerald-600 text-white";
                } else if (isActive && option.value === "not_met") {
                  activeClass = "bg-red-600 text-white";
                }

                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setStatusFilter(option.value)}
                    className={`h-7 w-full rounded px-2 text-xs font-semibold transition-colors ${
                      isActive
                        ? activeClass
                        : "bg-transparent text-gray-700 hover:bg-white/70"
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {requirements.length === 0 ? (
            <p className="py-6 text-sm text-gray-500">
              No readiness requirements for this disposition.
            </p>
          ) : visibleRequirements.length === 0 ? (
            <p className="py-6 text-sm text-gray-500">
              No criteria match this filter.
            </p>
          ) : (
            <div className="flex flex-col gap-2.5">
              {visibleRequirements.map(({ requirement, serialNumber }) => (
                <DcBarriersCriteriaCard
                  key={`${serialNumber}-${requirement.SignalName}`}
                  requirement={requirement}
                  serialNumber={serialNumber}
                  onEvidenceClick={onEvidenceClick}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DcBarriersPanel;
